import { DateLocalizer } from 'react-big-calendar';
import { getWeekEndMoment, getWeekStartMoment } from './date';

/*
 * Roads react-big-calendar date localizer.
 *
 * A copy of react-big-calendar's `momentLocalizer` (lib/localizers/moment.js, v1.19.x) with one change: the first
 * day of the week is passed in explicitly (the tenant "Week starts on", 0 = Sunday ... 6 = Saturday) instead of
 * being read from the active moment locale. firstOfWeek, startOf/endOf 'week', firstVisibleDay, lastVisibleDay and
 * visibleDays all use it, so the month grid, the week view and the fetch ranges agree on one week. Formats are
 * the library's, unchanged. moment's locale is never patched.
 *
 * Create it with useCalendarLocalizer (hooks/tenant.hooks), which memoises it on the language's week start.
 */

const weekRangeFormat = ({ start, end }, culture, local) => `${local.format(start, 'MMMM DD', culture)} – ${
    local.format(end, local.eq(start, end, 'month') ? 'DD' : 'MMMM DD', culture)}`;

const dateRangeFormat = ({ start, end }, culture, local) => `${local.format(start, 'L', culture)} – ${local.format(end, 'L', culture)}`;

const timeRangeFormat = ({ start, end }, culture, local) => `${local.format(start, 'LT', culture)} – ${local.format(end, 'LT', culture)}`;

const timeRangeStartFormat = ({ start }, culture, local) => `${local.format(start, 'LT', culture)} – `;

const timeRangeEndFormat = ({ end }, culture, local) => ` – ${local.format(end, 'LT', culture)}`;

export const formats = {
    dateFormat: 'DD',
    dayFormat: 'DD ddd',
    weekdayFormat: 'ddd',
    selectRangeFormat: timeRangeFormat,
    eventTimeRangeFormat: timeRangeFormat,
    eventTimeRangeStartFormat: timeRangeStartFormat,
    eventTimeRangeEndFormat: timeRangeEndFormat,
    timeGutterFormat: 'LT',
    monthHeaderFormat: 'MMMM YYYY',
    dayHeaderFormat: 'dddd MMM DD',
    dayRangeHeaderFormat: weekRangeFormat,
    agendaHeaderFormat: dateRangeFormat,
    agendaDateFormat: 'ddd MMM DD',
    agendaTimeFormat: 'LT',
    agendaTimeRangeFormat: timeRangeFormat,
};

const fixUnit = (unit) => {
    const datePart = unit ? unit.toLowerCase() : unit;

    if (datePart === 'FullYear') {
        return 'year';
    }

    return datePart || undefined;
};

/**
 * @param moment the moment (or moment-timezone) instance the calendar should use
 * @param weekStartsOn tenant "Week starts on", 0 = Sunday ... 6 = Saturday
 */
export default function createWeekStartLocalizer(moment, weekStartsOn) {
    const locale = (m, c) => (c ? m.locale(c) : m);

    const getTimezoneOffset = (date) => moment(date).toDate().getTimezoneOffset();

    const getDstOffset = (start, end) => {
        // Offset in the timezone of the events (local), not in the timezone of the calendar.
        const st = moment(start).local();
        const ed = moment(end).local();

        if (!moment.tz) {
            return st.toDate().getTimezoneOffset() - ed.toDate().getTimezoneOffset();
        }

        const tzName = st?._z?.name ?? moment.tz.guess();
        const startOffset = moment.tz.zone(tzName).utcOffset(+st);
        const endOffset = moment.tz.zone(tzName).utcOffset(+ed);

        return startOffset - endOffset;
    };

    const getDayStartDstOffset = (start) => getDstOffset(moment(start).startOf('day'), start);

    // 'week' resolves against the tenant week start, every other unit is moment's own.
    const startOfUnit = (date, datePart) => (datePart === 'week'
        ? getWeekStartMoment(date, weekStartsOn)
        : moment(date).startOf(datePart));

    const endOfUnit = (date, datePart) => (datePart === 'week'
        ? getWeekEndMoment(date, weekStartsOn)
        : moment(date).endOf(datePart));

    const defineComparators = (a, b, unit) => {
        const datePart = fixUnit(unit);
        const dtA = datePart ? startOfUnit(a, datePart) : moment(a);
        const dtB = datePart ? startOfUnit(b, datePart) : moment(b);

        return [dtA, dtB, datePart];
    };

    const startOf = (date = null, unit) => {
        const datePart = fixUnit(unit);

        return datePart ? startOfUnit(date, datePart).toDate() : moment(date).toDate();
    };

    const endOf = (date = null, unit) => {
        const datePart = fixUnit(unit);

        return datePart ? endOfUnit(date, datePart).toDate() : moment(date).toDate();
    };

    const eq = (a, b, unit) => {
        const [dtA, dtB, datePart] = defineComparators(a, b, unit);

        return dtA.isSame(dtB, datePart);
    };

    const neq = (a, b, unit) => !eq(a, b, unit);

    const gt = (a, b, unit) => {
        const [dtA, dtB, datePart] = defineComparators(a, b, unit);

        return dtA.isAfter(dtB, datePart);
    };

    const lt = (a, b, unit) => {
        const [dtA, dtB, datePart] = defineComparators(a, b, unit);

        return dtA.isBefore(dtB, datePart);
    };

    const gte = (a, b, unit) => {
        const [dtA, dtB, datePart] = defineComparators(a, b, unit);

        return dtA.isSameOrAfter(dtB, datePart);
    };

    const lte = (a, b, unit) => {
        const [dtA, dtB, datePart] = defineComparators(a, b, unit);

        return dtA.isSameOrBefore(dtB, datePart);
    };

    const inRange = (day, min, max, unit = 'day') => {
        const datePart = fixUnit(unit);

        return moment(day).isBetween(moment(min), moment(max), datePart, '[]');
    };

    const min = (dateA, dateB) => moment.min(moment(dateA), moment(dateB)).toDate();

    const max = (dateA, dateB) => moment.max(moment(dateA), moment(dateB)).toDate();

    const merge = (date, time) => {
        if (!date && !time) return null;

        const tm = moment(time).format('HH:mm:ss');
        const dt = moment(date).startOf('day').format('MM/DD/YYYY');

        // Built from strings to avoid issues when switching timezones.
        return moment(`${dt} ${tm}`, 'MM/DD/YYYY HH:mm:ss').toDate();
    };

    const add = (date, adder, unit) => moment(date).add(adder, fixUnit(unit)).toDate();

    const range = (start, end, unit = 'day') => {
        const datePart = fixUnit(unit);
        const days = [];
        let current = moment(start).toDate();

        while (lte(current, end)) {
            days.push(current);
            current = add(current, 1, datePart);
        }

        return days;
    };

    const ceil = (date, unit) => {
        const datePart = fixUnit(unit);
        const floor = startOf(date, datePart);

        return eq(floor, date) ? floor : add(floor, 1, datePart);
    };

    const diff = (a, b, unit = 'day') => moment(b).diff(moment(a), fixUnit(unit));

    const minutes = (date) => moment(date).minutes();

    const firstOfWeek = () => weekStartsOn;

    const firstVisibleDay = (date) => getWeekStartMoment(moment(date).startOf('month'), weekStartsOn).toDate();

    const lastVisibleDay = (date) => getWeekEndMoment(moment(date).endOf('month'), weekStartsOn).toDate();

    const visibleDays = (date) => {
        const last = lastVisibleDay(date);
        const days = [];
        let current = firstVisibleDay(date);

        while (lte(current, last)) {
            days.push(current);
            current = add(current, 1, 'd');
        }

        return days;
    };

    const getSlotDate = (dt, minutesFromMidnight, offset) => moment(dt)
        .startOf('day')
        .minute(minutesFromMidnight + offset)
        .toDate();

    // moment handles DST differences in its own calculations.
    const getTotalMin = (start, end) => diff(start, end, 'minutes');

    const getMinutesFromMidnight = (start) => {
        const dayStart = moment(start).startOf('day');

        return moment(start).diff(dayStart, 'minutes') + getDayStartDstOffset(start);
    };

    const continuesPrior = (start, first) => moment(start).isBefore(moment(first), 'day');

    const continuesAfter = (start, end, last) => moment(end).isSameOrAfter(moment(last), 'minutes');

    const daySpan = (start, end) => moment.duration(moment(end).diff(moment(start))).days();

    const sortEvents = ({ evtA, evtB }) => {
        const { start: aStart, end: aEnd, allDay: aAllDay } = evtA;
        const { start: bStart, end: bEnd, allDay: bAllDay } = evtB;
        const startSort = +startOf(aStart, 'day') - +startOf(bStart, 'day');
        const durA = daySpan(aStart, aEnd);
        const durB = daySpan(bStart, bEnd);

        return startSort // start day first
            || durB - durA // events spanning multiple days go first
            || !!bAllDay - !!aAllDay // then all-day single day events
            || +aStart - +bStart // then start time
            || +aEnd - +bEnd; // then end time
    };

    const inEventRange = ({ event, range: eventRange }) => {
        const startOfDay = moment(event.start).startOf('day');
        const eEnd = moment(event.end);
        const rStart = moment(eventRange.start);
        const rEnd = moment(eventRange.end);
        const startsBeforeEnd = startOfDay.isSameOrBefore(rEnd, 'day');
        // A zero-duration event is handled a little differently.
        const sameMin = !startOfDay.isSame(eEnd, 'minutes');
        const endsAfterStart = sameMin
            ? eEnd.isAfter(rStart, 'minutes')
            : eEnd.isSameOrAfter(rStart, 'minutes');

        return startsBeforeEnd && endsAfterStart;
    };

    const isSameDate = (date1, date2) => moment(date1).isSame(moment(date2), 'day');

    // Used by eventLevels to size an event's span when the calendar timezone is ahead of the browser's.
    const browserTZOffset = () => {
        const dt = new Date();
        const neg = /-/.test(dt.toString()) ? '-' : '';
        const comparator = Number(`${neg}${Math.abs(dt.getTimezoneOffset())}`);

        return moment().utcOffset() > comparator ? 1 : 0;
    };

    return new DateLocalizer({
        formats,
        firstOfWeek,
        firstVisibleDay,
        lastVisibleDay,
        visibleDays,
        format: (value, format, culture) => locale(moment(value), culture).format(format),
        lt,
        lte,
        gt,
        gte,
        eq,
        neq,
        merge,
        inRange,
        startOf,
        endOf,
        range,
        add,
        diff,
        ceil,
        min,
        max,
        minutes,
        getSlotDate,
        getTimezoneOffset,
        getDstOffset,
        getTotalMin,
        getMinutesFromMidnight,
        continuesPrior,
        continuesAfter,
        sortEvents,
        inEventRange,
        isSameDate,
        daySpan,
        browserTZOffset,
    });
}
